'use client';

import type { PropsWithChildren } from 'react';
import type { Collection } from '@/app/actions/collection';
import type { AnswerType } from '~/model/AnswerType';
import type { CommentType } from '~/model/CommentType';
import type { ConclusionType } from '~/model/ConclusionType';
import type { InteractionType } from '~/model/InteractionType';
import type { QuestionReconstructedType } from '~/model/QuestionType';
import type { ResultsType } from '~/model/ResultsType';
import type { SectionType } from '~/model/SectionType';
import { useLocale, useTranslations } from 'next-intl';
import { Fragment, useState } from 'react';
import { MainColumn } from '@/app/components/MainColumn';
import { CommentsProvider } from '@/app/components/Provider/CommentsProvider';
import { Translate } from '@/app/components/Translate/Translate';
import { buildQuestionsToDisplay } from '@/app/utils/entities/questions';
import { getEntityTranslationVersion, getLangAttributeValue } from '@/app/utils/entities/translation';
import { BeardmanTalking } from '~/components/Beardman/BeardmanTalking/BeardmanTalking';
import { EntityPanel } from '~/components/Interaction/Form/EntityPanel';
import { Result } from '~/components/Interaction/Survey/Results/Types/Result';
import { journeyColumnTemplate } from '~/components/Journey/config';
import { apiTypes } from '~/const/api';
import { ComponentTranslationStatus } from '~/const/appConst';
import { AcceptLanguage } from '~/const/locale';

export const Results = ({
  children,
  answers,
  comments,
  conclusions,
  interaction,
  questions,
  results,
  sections,
}: PropsWithChildren<{
  answers?: Array<AnswerType>;
  comments?: Array<{
    [key: number]: Collection<AnswerType & CommentType>;
  }>;
  conclusions: Array<ConclusionType>;
  interaction: InteractionType;
  questions: Array<QuestionReconstructedType>;
  results?: ResultsType;
  sections: Array<SectionType>;
}>) => {
  const t = useTranslations('SURVEY.RESULTS');
  const locale = useLocale();

  const [interactionTranslated, setInteractionTranslated] = useState(false);

  const isEnTranslationValid = interaction?.translations?.en_GB?.status === ComponentTranslationStatus.valid;
  const originalLocale = interaction?.original_locale;

  const getTranslationInteraction = () => {
    setInteractionTranslated(!interactionTranslated);
  };

  const interactionData = getEntityTranslationVersion({
    entity: interaction,
    entityTranslated: interactionTranslated,
    isEnTranslationValid,
    originalLocale,
    userLanguage: AcceptLanguage[locale],
  });

  const isValidTranslated = interactionData?.status === ComponentTranslationStatus.valid;
  const langValue = getLangAttributeValue(originalLocale, AcceptLanguage[locale]);

  const questionsToDisplay = answers
    ? buildQuestionsToDisplay({
        questions,
        conclusions,
        answers,
      })
    : questions;

  const filteredQuestions = questionsToDisplay.filter(question => [
    apiTypes.CHECK,
    apiTypes.CONDITIONAL,
    apiTypes.RADIO,
    apiTypes.STAR_RATING,
    apiTypes.RANKING,
    apiTypes.RANKING_PICTURE,
    apiTypes.VAS1,
    apiTypes.VAS2,
    apiTypes.RANKING_SCALE,
    apiTypes.SELECT,
    apiTypes.SCALE,
    apiTypes.GRID,
    apiTypes.CHOICES_JAR,
  ].includes(question.type));

  const resultsUris = Object.keys(results.questions?.[0] || {}).filter(
    result => filteredQuestions.some(question => question['@id'] === result),
  );

  const resultTodisplay = filteredQuestions.filter((question) => {
    return (
      resultsUris?.find(uri => uri === question['@id'])
      && results.questions?.[0]?.[question['@id']]?.participants_count > 0
    );
  });

  const resultsMessage = t(resultTodisplay?.length > 0 ? 'INFO' : 'INFO_2');

  return (
    <section className="results">
      <MainColumn column={journeyColumnTemplate}>
        <Translate
          getTranslation={getTranslationInteraction}
          originalLanguage={originalLocale}
          userLanguage={AcceptLanguage[locale]}
          translated={interactionTranslated}
          entityUri={interaction['@id']}
          isValidTranslated={isValidTranslated}
          isEnTranslationValid={isEnTranslationValid}
        />
      </MainColumn>
      <BeardmanTalking
        {...answers
          ? {
              title: (
                <p lang={langValue} data-testid="contribution-confirmed">
                  {t('CONTRIBUTION_CONFIRMED')}
                </p>
              ),
              description: (
                <p lang={langValue} data-testid="results-info">
                  {resultsMessage}
                </p>
              ),
            }
          : {
              title: interactionData.title,
            }}
      />
      <MainColumn column={journeyColumnTemplate}>
        {resultsUris?.length > 0
          ? resultTodisplay.map((question, index) => {
              const questionId = question['@id'];
              const shouldDisplaySection = question.section !== filteredQuestions[index - 1]?.section;
              const displaySection = index === 0 || shouldDisplaySection;
              const section = sections?.find(section => section['@id'] === question.section);
              const result = results.questions[0][questionId];

              return (
                <Fragment key={question?.['@id']}>
                  {section && displaySection && (
                    <EntityPanel
                      entityPanel={section}
                      langValue={langValue}
                    />
                  )}
                  <CommentsProvider
                    answers={comments?.[index][result.result_id]}
                    interaction={interaction}
                    question={question}
                    resultId={result.result_id}
                  >
                    <Result
                      question={question}
                      result={result}
                    />
                  </CommentsProvider>
                </Fragment>
              );
            })
          : (
              <div className="panel">
                <footer className="panel__body">
                  <span className="no-result">{t('NO_RESULT')}</span>
                </footer>
              </div>
            )}
      </MainColumn>
      {children}
    </section>
  );
};
